<script setup>
import store from '@/store'
import { ref, computed, getCurrentInstance, watch } from 'vue'
import { cloneDeep } from 'lodash-es'
import { download, getDatedFilename, getErrorMessage, HAZARD_URL, SEARCH_URL, SERVICES_URL } from '@/main'
import ExportMenu from '@/components/ExportMenu.vue'
import TransferMenu from '@/components/TransferMenu.vue'
import HazardPopover from '@/components/hazard/HazardPopover.vue'
import ChemPopover from '@/components/chemical/ChemPopover/ChemPopover.vue'
import { getChemicalIdentifier } from '@/utils/chemical'

const props = defineProps({
  searchResult: { type: Object, required: true },
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['loadChemical'])

const { proxy } = getCurrentInstance()

const searchType = computed(() => props.searchResult?.request?.searchType)

const isSelectionMode = ref(false)
const downloadingAll = ref(false)
const downloadingMol = ref({})
const downloading = ref(false)

const feature = computed(() => store.getters.feature)

const records = computed(() => props.searchResult?.records)

const isMassInQuery = computed(
  () => props.searchResult?.request?.params['min-mass'] || props.searchResult?.request?.params['max-mass'] || false,
)

const minSimilarity = computed(() => props.searchResult?.request?.params['min-similarity'])
const querySmiles = computed(() => props.searchResult?.request?.smiles || props.searchResult?.request?.query)

const addId = (record) => ({ ...record, id: record.sid })
const normalizeTransferRecords = (records) => records.map(addId)

const recordsForTransfer = ref(normalizeTransferRecords(records.value))

const selectAll = (structures) => (recordsForTransfer.value = normalizeTransferRecords(structures))
const clearSelection = () => (recordsForTransfer.value = [])

watch(records, (newVal) => {
  if (!isSelectionMode.value) {
    selectAll(newVal)
    return
  }
})

const toggleSelectionAll = (val) => {
  if (val) return clearSelection()
  selectAll(records.value)
}

const findItemInSelected = (item) =>
  recordsForTransfer.value.find((record) => getChemicalIdentifier(record) === getChemicalIdentifier(item))

const toggleSelected = (item) => {
  const findRecord = findItemInSelected(item)
  if (findRecord)
    return (recordsForTransfer.value = [
      ...recordsForTransfer.value.filter((record) => getChemicalIdentifier(record) !== getChemicalIdentifier(item)),
    ])
  recordsForTransfer.value.push(addId(item))
}

const getImageUrl = (item) => {
  if (
    searchType.value === 'SUBSTRUCTURE' ||
    searchType.value === 'SIMILAR' ||
    searchType.value === 'TOXPRINTS' ||
    searchType.value === 'ADVANCED'
  ) {
    return `${SEARCH_URL}/render/svg?id=${item.id}&query=${encodeURIComponent(querySmiles.value)}`
  } else {
    return `${SEARCH_URL}/render/svg?id=${item.id}`
  }
}

const getDashboardLink = (item) => `https://comptox.epa.gov/dashboard/${item.sid}`

const isSimSearchType = () =>
  searchType.value === 'SUBSTRUCTURE' ||
  searchType.value === 'SIMILAR' ||
  searchType.value === 'TOXPRINTS' ||
  (searchType.value === 'ADVANCED' && minSimilarity.value > 0)

const roundup = (value, digits) => {
  const round = Math.round(Math.pow(10, digits))
  return Math.round((value + Number.EPSILON) * round) / round
}

const loadChemical = (item) => emit('loadChemical', item)

const onDownload = (item, format) => {
  downloadingMol.value[item.id] = true

  proxy.$axios
    .get(`${SEARCH_URL}/download/mol?id=${item.id}&format=${format}`)
    .then((response) => {
      download(`${item.gsid ?? item.id}_${format}.mol`, response.data)
    })
    .catch((error) => {
      console.log(error)
    })
    .finally(() => {
      downloadingMol.value[item.id] = false
    })
}

const getHazardChemical = (sid) =>
  proxy.$axios.get(`${HAZARD_URL}?query=${sid}`).then((response) => response.data.hazardChemicals[0])

const downloadAll = (format) => {
  downloadingAll.value = true
  if (props.searchResult && props.searchResult.request) {
    const requestCopy = cloneDeep(props.searchResult.request)
    requestCopy['limit'] = props.searchResult.totalRecordsCount
    requestCopy['offset'] = 0

    proxy.$axios
      .post(SEARCH_URL, requestCopy, { timeout: 60 * 1000 })
      .then((response) => {
        const filename = getDatedFilename('search', format.toLowerCase())
        downloadFile(response.data, format, filename)
        downloadingAll.value = false
      })
      .catch((error) => {
        let errorMessage = getErrorMessage(error)
        if (error.status === 0) errorMessage = 'Error. Search timed out.'

        if (searchType.value === 'FORMULA' && typeof error?.json === 'function') {
          error.json().then((data) => {
            if (data.error) {
              if (typeof proxy.showCalculateError === 'function') proxy.showCalculateError('Error: ' + data.error)
            }
          })
        } else {
          if (typeof proxy.showCalculateError === 'function') proxy.showCalculateError(errorMessage)
          else console.error(errorMessage)
        }

        // props.searchResult['totalRecordsCount'] = 0
        downloadingAll.value = false
      })
  }
}

const downloadFile = (inputData, format, file_name) => {
  const itemsList = inputData.records.map((item) => ({ id: item.id, sim: item.similarity }))
  const queryBody = {
    ids: itemsList,
    format: format,
    query:
      searchType.value === 'SUBSTRUCTURE' ||
      searchType.value === 'SIMILAR' ||
      searchType.value === 'TOXPRINTS' ||
      searchType.value === 'ADVANCED'
        ? querySmiles.value
        : '',
  }

  proxy.$axios
    .post(`${SEARCH_URL}/download`, queryBody, { responseType: 'arraybuffer' })
    .then((response) => {
      download(file_name, response.data, format)
    })
    .catch((error) => {
      console.log(error)
    })
}
</script>

<template>
  <div class="d-flex flex-column gap-2">
    <v-progress-linear
      v-if="loading"
      indeterminate
    />

    <div
      v-if="records && records.length !== 0"
      class="d-flex flex-row align-items-center justify-content-between flex-wrap gap-4"
    >
      <div class="d-flex flex-row align-items-center gap-2">
        <v-switch
          v-model="isSelectionMode"
          :label="`Selection mode (${recordsForTransfer.length}/${records.length})`"
          hide-details
          @change="(val) => toggleSelectionAll(val)"
        />

        <div v-if="isSelectionMode">
          <v-btn-toggle dense>
            <v-btn
              v-b-tooltip
              title="Select All"
              @click="() => selectAll(records)"
              aria-label="Select All"
            >
              <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
            </v-btn>
            <v-btn
              v-b-tooltip
              title="Select None"
              @click="clearSelection"
              aria-label="Select None"
            >
              <v-icon>mdi-checkbox-multiple-blank-outline</v-icon>
            </v-btn>
          </v-btn-toggle>
        </div>
      </div>

      <div class="d-flex flex-row align-items-center gap-2">
        <b-dropdown
          v-b-tooltip
          dropdown
          title="Download search results"
        >
          <template slot="button-content">
            <span>
              Download All
              <b-spinner
                v-if="downloadingAll"
                small
              />
            </span>
          </template>

          <b-dropdown-item @click="downloadAll('sdf')">SDF</b-dropdown-item>
          <b-dropdown-item @click="downloadAll('xlsx')">XLSX</b-dropdown-item>
        </b-dropdown>

        <ExportMenu
          v-model="downloading"
          :export-url="`${SERVICES_URL}/export`"
          :selected-structures="recordsForTransfer"
          show-images
        />

        <TransferMenu
          :records="recordsForTransfer"
          dropdown-mode
          only-icon
          :ui="{ root: 'gap-2' }"
        />
      </div>
    </div>

    <div
      v-if="records && records.length !== 0"
      class="d-flex flex-row flex-wrap"
    >
      <div
        v-for="item in records"
        :key="`r-${item.id}`"
        :class="isSelectionMode ? 'tile-selection tile-img' : 'tile-img'"
      >
        <a
          :href="getDashboardLink(item)"
          target="_blank"
          :aria-label="`View structure for record ${item.id}`"
        >
          <img
            :id="`r-${item.id}`"
            :src="getImageUrl(item)"
            alt=""
          />
        </a>

        <div
          v-if="isSelectionMode"
          class="badge-top-left"
          style="margin-top: 0"
        >
          <v-checkbox
            class="selection-checkbox"
            :value="!!findItemInSelected(item)"
            @change="() => toggleSelected(item)"
          />
        </div>
        <div
          v-else
          class="badge-top-left hiding-badge"
        >
          <v-btn
            icon
            small
            @click="loadChemical(item)"
            aria-label="Load Chemical"
          >
            <v-icon>mdi-upload</v-icon>
          </v-btn>
        </div>

        <div class="badge-bottom-left hiding-badge">
          <v-menu
            open-on-hover
            right
            offset-x
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                :loading="downloadingMol?.[item.id]"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </template>

            <v-list
              dense
              class="py-0 my-0"
            >
              <v-list-item
                aria-label="Download v2000"
                @click="onDownload(item, 'v2000')"
              >
                <v-list-item-title>v2000</v-list-item-title>
              </v-list-item>
              <v-list-item
                aria-label="Download v3000"
                @click="onDownload(item, 'v3000')"
              >
                <v-list-item-title>v3000</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <div
          v-if="item.hazard && feature('hazard')"
          :id="`hazard-${item.id}`"
          :class="{
            'table-secondary': item.hazard === 'I',
            'table-warning-light': item.hazard === 'M',
            'table-success': item.hazard === 'L',
            'table-danger': item.hazard === 'VH',
            'table-orange': item.hazard === 'H',
            'authoritative': item.authority === 'Authoritative',
            'screening': item.authority === 'Screening',
            'predicted': item.authority === 'QSAR Model',
          }"
          class="badge-top-right"
        >
          {{ item.hazard }}
          <HazardPopover
            :hazard-chemical="getHazardChemical(item.chemId)"
            :target="`hazard-${item.id}`"
            first-only
          />
        </div>
        <div
          v-if="isSimSearchType() && item.similarity != null"
          :class="'badge-bottom-right' + (isMassInQuery ? ' move-up' : '')"
          v-b-tooltip
          title="Similarity"
        >
          {{ item.similarity | number('0.00') }}
        </div>

        <div
          v-if="isMassInQuery"
          v-b-tooltip
          :title="roundup(item.mass ?? item.monoisotopicMass, 6)"
          class="badge-bottom-right"
        >
          {{ roundup(item.mass ?? item.monoisotopicMass, 3) }}
        </div>

        <ChemPopover
          :chemical="item"
          :query="querySmiles"
          :search-type="searchType"
          :target="`r-${item.id}`"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tile-img {
  width: 110px;
  height: 110px;
  border: 1px solid #ddd;
  margin: 2px;
  padding: 5px;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-width: 100%;
    max-height: 100%;
  }

  position: relative;

  .badge-top-left {
    position: absolute;
    top: 0;
    left: 0;
    padding: 3px;
  }

  .badge-top-right {
    position: absolute;
    top: 0;
    right: 0;
    padding: 3px;
  }

  .badge-bottom-right {
    position: absolute;
    bottom: 0;
    right: 0;
    padding: 0 2px;
    background: rgba(#efefef, 0.5);
  }

  .badge-bottom-right.move-up {
    bottom: 20px;
  }

  .badge-bottom-left {
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 0 2px;
  }

  .hiding-badge {
    opacity: 0;
    cursor: pointer;
    display: flex;
    transition: 1s;

    & > i {
      opacity: 0.7;
    }

    a {
      display: none;
      width: 50%;
      font-size: smaller;
      z-index: 1;
    }

    &:hover {
      width: 100%;
      text-decoration: none;

      a {
        display: block;

        &:hover {
          background: #efefef;
        }
      }
    }
  }

  &:hover .hiding-badge {
    opacity: 1;
  }
}

.selection-checkbox {
  margin-top: 0;
}

.tile-selection {
  border-color: #afc0ea;
}

.v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}
</style>
